// Search orchestration for the discovery sub-tab: debounced server search pass, gated
// on visibility (hidden tabs must not churn), free-text query support (the search bar
// feeds the endpoint's searchTerm), segmented into mine/office/market, scored against
// the contact's search profile, blacklist- and duplicate-aware ordering, with a
// session-cache layer so remounting the page reuses fresh results instantly.
//
// NOTE (2026-08-10): this hook used to take an Algolia index from
// AlgoliaClientContext and bail out when it was null. Since the Postgres cutover
// that index is ALWAYS null, so the bail-out ran on every pass and the tab sat on
// permanent skeletons. There is no client-side search handle any more — the search
// authenticates itself with the user's Firebase token inside postListingsSearch — so
// there is nothing left to gate on. Don't reintroduce a "search client ready" guard.
import { useCallback, useEffect, useRef, useState } from "react";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import type { RealEstateContact } from "../../../../../../@types/sharedSchema";
import { buildFiltersFromContact, comparableSearchFilters } from "./contactMatchFilters";
import type { MatchFilters } from "./contactMatchFilters";
import {
  searchMatchListings,
  segmentMatchHits,
  findYad2MadlanDuplicateIds
} from "./matchesSearch";
import type { MatchHit, MatchSegmentId } from "./matchesSearch";
import { computeMatchScore } from "./matchScore";
import { fetchBlacklistCached, searchCacheKey } from "./matchesData";
import { getCached, setCached } from "../../data/sessionCache";

export type ScoredMatchHit = {
  hit: MatchHit;
  score: number | null;
  /** Blacklisted or yad2-duplicate-of-madlan — kept visible but sorted last (legacy UX). */
  deprioritized: boolean;
};

export type MatchesSearchState = {
  segments: Record<MatchSegmentId, ScoredMatchHit[]>;
  /** Approximate market total (sum of nbHits) — for the "N+" segment caption only. */
  marketApproxTotal: number;
  loading: boolean;
  loadedOnce: boolean;
  /** Last search pass failed (before ever succeeding ⇒ the UI offers a retry). */
  error?: boolean;
  filtersKey?: string;
  loadedAt?: number;
};

export type MatchesSearch = MatchesSearchState & {
  /** Re-run the search after a failure (clears the error and re-fires the pass). */
  retry: () => void;
};

const EMPTY_STATE: MatchesSearchState = {
  segments: { mine: [], office: [], market: [] },
  marketApproxTotal: 0,
  loading: false,
  loadedOnce: false
};

const SEARCH_DEBOUNCE_MS = 300;
const RESULTS_FRESH_MS = 2 * 60 * 1000;

const publishedMillis = (hit: MatchHit): number => {
  const date = hit.listingPublishedTime ?? hit.createdTime;
  const ms = date instanceof Date ? date.getTime() : new Date(date as unknown as string).getTime();
  return isNaN(ms) ? 0 : ms;
};

function scoreAndSort(
  hits: MatchHit[],
  profile: MatchFilters,
  deprioritizedIds: Set<string>
): ScoredMatchHit[] {
  return hits
    .map((hit) => ({
      hit,
      score: computeMatchScore(hit, profile),
      deprioritized: deprioritizedIds.has(hit.objectID)
    }))
    .sort((a, b) => {
      if (a.deprioritized !== b.deprioritized) return a.deprioritized ? 1 : -1;
      const aScore = a.score ?? -1;
      const bScore = b.score ?? -1;
      if (bScore !== aScore) return bScore - aScore;
      return publishedMillis(b.hit) - publishedMillis(a.hit);
    });
}

export function useMatchesSearch(
  contact: RealEstateContact,
  filters: MatchFilters,
  enabled: boolean,
  queryText: string
): MatchesSearch {
  const { user } = useAuth();
  const { organizations, isLoadingOrganization } = useFirestore();

  const [state, setState] = useState<MatchesSearchState>(
    () => getCached<MatchesSearchState>(searchCacheKey(contact.id)) ?? EMPTY_STATE
  );
  const [retryNonce, setRetryNonce] = useState(0);

  // Latest contact for profile scoring without making the search re-run on every live
  // contact snapshot (the filters key is the real search identity).
  const contactRef = useRef(contact);
  contactRef.current = contact;

  const requestSeq = useRef(0);
  const normalizedQuery = queryText.trim();
  // FULL filter tier (chips + drawer) — an advanced filter left out of this key would
  // be edited in the drawer and never re-run the search. Also the result-set identity
  // the source-tab auto-select keys on (see useAddListingsSourceTab).
  const filtersKey = `${JSON.stringify(comparableSearchFilters(filters))}|q:${normalizedQuery}`;
  const organizationIdsKey = (organizations ?? [])
    .map((org) => org.id)
    .sort()
    .join(",");

  useEffect(() => {
    if (!enabled || !user?.email || isLoadingOrganization) return;

    // Fresh results for the same filters+query (e.g. tab flipped away and back, or page
    // remount within the session) — reuse, no churn.
    const cached = getCached<MatchesSearchState>(searchCacheKey(contact.id));
    if (
      cached?.loadedOnce &&
      cached.filtersKey === filtersKey &&
      cached.loadedAt &&
      Date.now() - cached.loadedAt < RESULTS_FRESH_MS
    ) {
      setState(cached);
      return;
    }

    const seq = ++requestSeq.current;
    const userEmail = user.email;
    const organizationIds = organizationIdsKey ? organizationIdsKey.split(",") : [];

    setState((prev) => ({ ...prev, loading: true, error: false }));
    const timer = setTimeout(async () => {
      try {
        const [blacklist, result] = await Promise.all([
          fetchBlacklistCached(userEmail).catch(() => null),
          searchMatchListings(filters, { queryText: normalizedQuery })
        ]);
        if (seq !== requestSeq.current) return; // stale response — a newer search ran

        const deprioritizedIds = findYad2MadlanDuplicateIds(result.hits);
        (blacklist?.listingIds ?? []).forEach((id) => deprioritizedIds.add(id));

        const profile = buildFiltersFromContact(contactRef.current);
        const segments = segmentMatchHits(result.hits, { userEmail, organizationIds });
        const next: MatchesSearchState = {
          segments: {
            mine: scoreAndSort(segments.mine, profile, deprioritizedIds),
            office: scoreAndSort(segments.office, profile, deprioritizedIds),
            market: scoreAndSort(segments.market, profile, deprioritizedIds)
          },
          marketApproxTotal: result.originTotals.yad2 + result.originTotals.madlan,
          loading: false,
          loadedOnce: true,
          error: false,
          filtersKey,
          loadedAt: Date.now()
        };
        setState(next);
        setCached(searchCacheKey(contact.id), next);
      } catch (err) {
        console.error("MatchesTab: search failed", err);
        if (seq === requestSeq.current) {
          setState((prev) => ({ ...prev, loading: false, error: true }));
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    user?.email,
    isLoadingOrganization,
    organizationIdsKey,
    filtersKey,
    contact.id,
    retryNonce
  ]);

  const retry = useCallback(() => setRetryNonce((n) => n + 1), []);

  return { ...state, retry };
}
